import { Form, Formik } from 'formik';
import { useCallback, useState } from 'react';
import useSWR, { KeyedMutator } from 'swr';
import { useDispatch } from 'react-redux';
import { Box } from '@mui/material';

import { getValue, openFile, queryParams } from '@shared/commonFunctions';
import { Modal } from '@shared/components/modals/Modal';
import { SERVICES } from '@shared/constants/apiConfig';
import { Loader } from '@shared/enhancers/withLoader';
import { ModalHeader } from '@styles/StyledDiv';
import { FedexFirstInnerWrapper, FedexSecondInnerWrapper, FedexTypography, OuterFedexBoxWrapper } from '@styles/ContactManager/DealStyles';
import { setLastCommentsRefresh } from '@store/reducers/contactManagerSlice';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { AlertModal } from '@shared/components/modals/AlertModal';

import { IDeal } from '../../interfaces';
import { FedexLabelForm } from './FedexLabelForm';
import { FedExLabelHistory, ITracking } from './FedExLabelHistory';
import { IContactData, IFedexLabel } from './interfaces';
import { getLabel, submitFedexLabelForm } from './request';

interface IFedexModalProps {
  fedexModalPosition: number;
  handleClose: () => void;
  reloadDeal: KeyedMutator<IDeal>;
}

enum EStreetName {
  StreetName1 = 'Street Name',
  StreetName2 = 'Street Name 2',
}

let override = false;

export const FedexModal = ({ fedexModalPosition, handleClose, reloadDeal }: IFedexModalProps) => {
  const {
    data: contactData,
    error: isContactDataError
  } = useSWR(`/${SERVICES.DOCUMENT}/shipping/contacts/${queryParams()?.id}`);
  const { data: fedExLabelHistory, mutate: reloadFedexLabels } = useSWR<IFedexLabel[]>(
    `/${SERVICES.DOCUMENT}/shipping/contacts/${queryParams()?.id}/deals/${queryParams().dealId}/labels`,
  );
  const { data: deal } = useSWR(`/${SERVICES.DEAL}/deals/${queryParams().dealId}`);
  const { data: serviceTypes } = useSWR(`/${SERVICES.DOCUMENT}/shipping/provider/servicetype`);
  const { data: fedexTrackingData, mutate: reloadFedexTracking } = useSWR<ITracking[]>(
    `/${SERVICES.DOCUMENT}/shipping/contacts/${queryParams()?.id}/deals/${queryParams().dealId}/tracking`,
  );
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [fieldName, setFieldName] = useState('');
  const dispatch = useDispatch();

  const subtitleMessage = `${fieldName} should be max 34 characters. Please reduce length of the ${fieldName} field by checking 'Override Contact & Address Details'
  option and continue.`;
  let fedExLabelHistoryMapped = fedExLabelHistory;
  let fedexTrackingDataMapped = fedexTrackingData;

  contactData && (contactData.override = false);

  if (fedexTrackingData && fedexTrackingData.length) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fedexTrackingDataMapped = fedexTrackingData.slice().sort((i: any, j: any) => {
      return new Date(j.dateCreated).getDate() - new Date(i.dateCreated).getDate();
    });
  }
  if (fedExLabelHistory && fedExLabelHistory.length) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fedExLabelHistoryMapped = fedExLabelHistory.slice().sort((i: IFedexLabel, j: IFedexLabel) => {
      return new Date(j.createdOn).getDate() - new Date(i.createdOn).getDate();
    });
  }

  const isLoading = !contactData && !isContactDataError;

  const handleSubmit = useCallback(async (data: IContactData) => {
    if (data.streetName.length > 34 || data.streetName2?.length > 34) {
      setIsOpen(true);
      if (data.streetName.length > 34) {
        setFieldName(EStreetName.StreetName1);
      } else {
        setFieldName(EStreetName.StreetName2);
      }
    } else {
      setLoading(true);
      const payload = {
        fedexShippingAddress: {
          streetLines: [data?.streetName, data?.streetName2],
          city: data?.city,
          stateOrProvinceCode: data?.state,
          postalCode: data?.postalCode,
          countryCode: data?.countryCode,
        },
        fedexContact: {
          personName: data?.name,
          companyName: data?.companyName,
          phoneNumber: data?.phoneNumber,
        },
        contactId: queryParams()?.id,
        isLC: true,
        isReturnLabelSelected: data?.returnLabel,
        isContactDetailsOverriden: override,
        includeInReadyToShip: data?.includeInReadyToShip,
        serviceType: data?.fedexServiceType,
      };

      const response = await submitFedexLabelForm(payload, queryParams()?.dealId);
      setLoading(false);
      await reloadFedexLabels();
      await reloadFedexTracking();
      await reloadDeal?.();
      dispatch(setLastCommentsRefresh(new Date().toISOString()));

      let fedexLabels;
      
      if (response?.fedexShipReturnUrl) {
        fedexLabels = await getLabel(response.fedexShipReturnUrl);

        openFile(fedexLabels?.data?.document, 'pdf', fedexLabels?.data?.fileName);
      }

      if (response?.fedexShipToUrl) {  
        fedexLabels = await getLabel(response.fedexShipToUrl);

        openFile(fedexLabels?.data?.document, 'pdf', fedexLabels?.data?.fileName);
      }
    }
  }, [queryParams, override]);

  return (
    <Modal
      hideBackdrop
      title="Fedex Form"
      dialogWidth="80%"
      dialogMinHeight="50%"
      resetPosition={fedexModalPosition}
      open={!!fedexModalPosition}
      onClose={handleClose}
    >
      {isLoading ? <Loader /> : (
        <>
          <ModalHeader>
            <FedexTypography variant="h2" >
              {contactData?.name}
            </FedexTypography>

            <OuterFedexBoxWrapper>
              <FedexFirstInnerWrapper>
                <Box>Street Name:</Box>
                <Box>State:</Box>
                <Box>FedEx Service Type:</Box>
              </FedexFirstInnerWrapper>

              <FedexSecondInnerWrapper>
                <Box>{getValue(contactData, 'streetName')}</Box>
                <Box>{getValue(contactData, 'state')}</Box>
                <Box>{getValue(contactData, 'fedexServiceType')}</Box>
              </FedexSecondInnerWrapper>
            </OuterFedexBoxWrapper>

            <OuterFedexBoxWrapper>
              <FedexFirstInnerWrapper>
                <Box>Street Name 2:</Box>
                <Box>Postal Code:</Box>
                <Box>Company name:</Box>
              </FedexFirstInnerWrapper>

              <FedexSecondInnerWrapper>
                <Box>{getValue(contactData, 'streetName2')}</Box>
                <Box>{getValue(contactData, 'postalCode')}</Box>
                <Box>{getValue(contactData, 'companyName')}</Box>
              </FedexSecondInnerWrapper>
            </OuterFedexBoxWrapper>

            <OuterFedexBoxWrapper>
              <FedexFirstInnerWrapper>
                <Box>Phone Number:</Box>
                <Box>City:</Box>
                <Box>Country Code:</Box>
              </FedexFirstInnerWrapper>

              <FedexSecondInnerWrapper>
                <Box>{getValue(contactData, 'phoneNumber')}</Box>
                <Box>{getValue(contactData, 'city')}</Box>
                <Box>{getValue(contactData, 'countryCode')}</Box>
              </FedexSecondInnerWrapper>
            </OuterFedexBoxWrapper>
          </ModalHeader>

          <Formik
            enableReinitialize
            initialValues={contactData || ({} as IContactData)}
            onSubmit={handleSubmit}
          >
            {(props) => (
              <Form onSubmit={props.handleSubmit}>
                <LoaderBackdrop open={loading} />
                <FedexLabelForm
                  serviceTypes={serviceTypes}
                  contactData={contactData}
                  loading={loading}
                  onChange={(value) => (override = value)}
                  {...props}
                />
                <FedExLabelHistory
                  data={fedExLabelHistoryMapped as IFedexLabel[]}
                  numericDealId={deal?.numericId}
                  dealId={queryParams()?.dealId ?? ''}
                  fedexTrackingData={fedexTrackingDataMapped as ITracking[]}
                  {...props}
                />
              </Form>
            )}
          </Formik>
        </>
      )}
      {isOpen &&
        <AlertModal
          hideCancel
          open={isOpen}
          title="Alert"
          subtitle={subtitleMessage}
          buttonText="Ok"
          maxWidth="450px"
          onClose={() => setIsOpen(false)}
          onConfirm={() => setIsOpen(false)}
        />
      }
    </Modal>
  );
};
